// CANVAS-CORE (A14): the Live canvas in the browser. The server folds the op log into a
// `CanvasState` (`liveCanvas.get`, already filtered to the blocks this viewer may read); the live
// channel then sends each new op (`canvas.op`) and run step (`run.step`). This module is the
// pure part: the state's shape, `applyOp` (a mirror of services/api/src/canvas/ops.ts, which the
// web cannot import: show upserts by id or replaces, arrange reorders and sets the layout,
// highlight only points at a block that is there, clear drops some blocks or all), and the
// steps list, deduplicated by (run_id, seq) because a reconnect replays what it missed.
import type { CanvasBlock, CanvasOp, CanvasState } from "@companyos/schema/contracts";

export type { CanvasBlock, CanvasOp, CanvasState };
export type CanvasBlockKind = CanvasBlock["kind"];

/** `run.step` frame data (LiveRunStep): one step of a run the viewer may view. */
export type LiveRunStep = {
  run_id: string;
  seq: number;
  kind: string;
  tool: string | null;
  summary: string | null;
  model: string | null;
  duration_ms: number | null;
  at: string;
};

/** `canvas.op` frame data (LiveCanvasOp): a show keeps only the blocks this viewer may see. */
export type LiveCanvasOp = { id: string; session_id: string | null; op: CanvasOp; at: string };

/** Query keys: under the company, so a whole-company invalidation refetches the canvas too. */
export const canvasKeys = {
  all: (co: string) => ["company", co, "live-canvas"] as const,
  state: (co: string, session: string) => ["company", co, "live-canvas", session] as const,
  steps: (co: string, session: string) => ["company", co, "live-canvas", session, "steps"] as const,
};

/** One op applied to a canvas state (a new state; the input is left alone). */
export function applyOp(s: CanvasState, op: CanvasOp): CanvasState {
  switch (op.op) {
    case "show": {
      if (op.replace) return { ...s, blocks: [...op.blocks], highlight: null };
      const blocks = [...s.blocks];
      for (const b of op.blocks) {
        const at = blocks.findIndex((x) => x.id === b.id);
        if (at >= 0) blocks[at] = b;
        else blocks.push(b);
      }
      return { ...s, blocks };
    }
    case "arrange": {
      const rank = new Map(op.order.map((id, i) => [id, i]));
      const last = Number.MAX_SAFE_INTEGER;
      const blocks = [...s.blocks].sort(
        (a, b) => (rank.get(a.id) ?? last) - (rank.get(b.id) ?? last),
      );
      return { ...s, blocks, layout: op.layout ?? s.layout };
    }
    case "highlight":
      if (!s.blocks.some((b) => b.id === op.block_id)) return s;
      return {
        ...s,
        highlight: { block_id: op.block_id, item_id: op.item_id ?? null, note: op.note ?? null },
      };
    case "clear": {
      if (!op.block_ids) return { ...s, blocks: [], highlight: null, windows: {} };
      const gone = new Set(op.block_ids);
      const blocks = s.blocks.filter((b) => !gone.has(b.id));
      const highlight = s.highlight && gone.has(s.highlight.block_id) ? null : s.highlight;
      const windows = s.windows
        ? Object.fromEntries(Object.entries(s.windows).filter(([id]) => !gone.has(id)))
        : s.windows;
      return { ...s, blocks, highlight, ...(windows ? { windows } : {}) };
    }
    // LIVE-DESKTOP: the folded geometry kept current (the desk itself reacts to the op feed).
    case "tidy": {
      const named = op.block_ids ? new Set(op.block_ids) : null;
      const windows = { ...(s.windows ?? {}) };
      for (const b of s.blocks) {
        const g = windows[b.id];
        if (named ? !named.has(b.id) : g?.state === "pinned") continue;
        windows[b.id] = g
          ? { ...g, state: "docked" }
          : { x: 0, y: 0, w: 0, h: 0, z: 0, state: "docked" };
      }
      return { ...s, windows };
    }
    case "place": {
      const windows = { ...(s.windows ?? {}) };
      for (const w of op.windows) {
        if (!s.blocks.some((b) => b.id === w.id)) continue;
        const was = windows[w.id];
        windows[w.id] = {
          x: w.x,
          y: w.y,
          w: w.w,
          h: w.h,
          z: w.z ?? was?.z ?? 0,
          state: w.state ?? was?.state ?? "open",
        };
      }
      return { ...s, windows };
    }
    // CANVAS-BLOCKS: the view switch and the person's clicks leave the canvas as it was (the
    // Live page reads `focus_view` frames itself).
    case "focus_view":
    case "interact":
      return s;
  }
}

/** The ops folded in order. */
export const foldOps = (s: CanvasState, ops: readonly CanvasOp[]): CanvasState =>
  ops.reduce(applyOp, s);

/** A live `canvas.op` frame applied: the op, and the frame's id as the new resume cursor. */
export function applyFrame(s: CanvasState, frame: LiveCanvasOp, cursor?: string): CanvasState {
  return { ...applyOp(s, frame.op), cursor: cursor || s.cursor, updated_at: frame.at };
}

type FilterCard = {
  title: string;
  status: string;
  due_at: string | null;
  completed_at: string | null;
  assignee_seat_ids?: readonly string[];
};

/**
 * A board block's filter applied to its cards (the board screen has no filter to hand it):
 * open cards only; `due` by the viewer's local day (today, this week from Monday, overdue =
 * due before now); `status` exact; `query` in the title. Cards name seats, not members, so
 * `assignee_membership_id` matches through `assigneeSeats`, the seats that member holds (the
 * org chart's holders): a card assigned to any of them. Without the seats (not read yet) the
 * assignee part is not applied.
 */
export function filterCards<T extends FilterCard>(
  cards: readonly T[],
  filter: CanvasBlock["filter"],
  now = new Date(),
  assigneeSeats?: ReadonlySet<string> | null,
): T[] {
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfDay = new Date(
    startOfDay.getFullYear(),
    startOfDay.getMonth(),
    startOfDay.getDate() + 1,
  );
  const monday = new Date(startOfDay);
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  const nextMonday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 7);
  const q = filter?.query?.toLowerCase();
  return cards.filter((c) => {
    if (c.completed_at) return false;
    if (filter?.status && c.status !== filter.status) return false;
    if (q && !c.title.toLowerCase().includes(q)) return false;
    if (filter?.assignee_membership_id && assigneeSeats) {
      if (!(c.assignee_seat_ids ?? []).some((s) => assigneeSeats.has(s))) return false;
    }
    if (filter?.due) {
      if (!c.due_at) return false;
      const due = new Date(c.due_at);
      if (filter.due === "today" && !(due >= startOfDay && due < endOfDay)) return false;
      if (filter.due === "this_week" && !(due >= monday && due < nextMonday)) return false;
      if (filter.due === "overdue" && !(due < now)) return false;
    }
    return true;
  });
}

/** How many recent steps the strip keeps. */
export const STEPS_KEPT = 30;

const stepKey = (s: Pick<LiveRunStep, "run_id" | "seq">) => `${s.run_id}:${s.seq}`;

/**
 * New steps merged into the list: deduplicated by (run_id, seq) (a later copy replaces the
 * earlier), newest first, at most `max`.
 */
export function mergeSteps(
  list: readonly LiveRunStep[],
  incoming: readonly LiveRunStep[],
  max = STEPS_KEPT,
): LiveRunStep[] {
  const byKey = new Map<string, LiveRunStep>();
  for (const s of list) byKey.set(stepKey(s), s);
  for (const s of incoming) byKey.set(stepKey(s), s);
  return [...byKey.values()]
    .sort((a, b) => (a.at === b.at ? b.seq - a.seq : a.at < b.at ? 1 : -1))
    .slice(0, max);
}
